/**
 * BTPRO THEME SWIPER SLIDE SYSTEM
 * Modüler ve yapılandırılabilir slide sistemi
 * Swiper.js kütüphanesi üzerine kurulmuştur
 */

(function () {
  'use strict';

  /**
   * Utility: Debounce function
   * Bağımsız çalışması için buraya eklendi
   */
  function debounce(fn, delay = 300) {
    let timeoutId;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  /**
   * ============================================
   * BTPRO THEME SWIPER SLIDE CUSTOM ELEMENT
   * ============================================
   */

  class BtproThemeSwiperSlide extends HTMLElement {
    constructor() {
      super();
      this.swiperInstance = null;
      this.swiperElement = null;
      this.breakpoint = this.dataset.breakpoint || 'all';
      this.currentBreakpointActive = false;
      this.resizeHandler = debounce(() => this.checkBreakpoint(), 250);
    }

    connectedCallback() {
      // Wait for DOM to be fully loaded and Swiper library
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        // Check if Swiper is loaded, if not wait a bit
        if (typeof Swiper === 'undefined') {
          setTimeout(() => this.init(), 100);
        } else {
          this.init();
        }
      }
    }

    init() {
      this.setup();
    }

    setup() {
      // Find the swiper container (.swiper class)
      this.swiperElement = this.querySelector('.swiper');
      
      if (!this.swiperElement) {
        console.warn('BtproThemeSwiperSlide: No .swiper element found inside <btpro-theme-swiper-slide>');
        return;
      }

      // Check if Swiper library is loaded
      if (typeof Swiper === 'undefined') {
        console.error('BtproThemeSwiperSlide: Swiper library not loaded. Make sure Swiper.js is included before swiper-slide.js');
        return;
      }

      // Apply CSS custom properties from data attributes
      this.applyCSSVariables();

      // Initial setup based on breakpoint
      this.checkBreakpoint();

      // Listen for resize events
      window.addEventListener('resize', this.resizeHandler);
    }

    /**
     * Data attribute'ları okuyup CSS custom properties'e çevirir
     * Örnek: data-swiper-theme-color="#ff0000" -> --btpro-swiper-theme-color: #ff0000
     */
    applyCSSVariables() {
      const cssVarMap = {
        // Theme & Base
        'swiperThemeColor': '--btpro-swiper-theme-color',
        'swiperPreloaderColor': '--btpro-swiper-preloader-color',
        
        // Navigation
        'swiperNavSize': '--btpro-swiper-nav-size',
        'swiperNavColor': '--btpro-swiper-nav-color',
        'swiperNavBg': '--btpro-swiper-nav-background',
        'swiperNavPadding': '--btpro-swiper-nav-padding',
        'swiperNavType': '--btpro-swiper-nav-type',
        'swiperNavBorderWidth': '--btpro-swiper-nav-border-width',
        'swiperNavTopOffset': '--btpro-swiper-nav-top-offset',
        'swiperNavSidesOffset': '--btpro-swiper-nav-sides-offset',
        
        // Pagination
        'swiperPaginationColor': '--btpro-swiper-pagination-color',
        'swiperPaginationType': '--btpro-swiper-pagination-type',
        'swiperPaginationHeightLines': '--btpro-swiper-pagination-height-lines',
        'swiperPaginationBgNumbers': '--btpro-swiper-pagination-bg-numbers',
        'swiperPaginationBgProgress': '--btpro-swiper-pagination-bg-progress',
        'swiperPaginationLeft': '--btpro-swiper-pagination-left',
        'swiperPaginationRight': '--btpro-swiper-pagination-right',
        'swiperPaginationTop': '--btpro-swiper-pagination-top',
        'swiperPaginationBottom': '--btpro-swiper-pagination-bottom',
        'swiperPaginationFractionColor': '--btpro-swiper-pagination-fraction-color',
        'swiperPaginationProgressbarBg': '--btpro-swiper-pagination-progressbar-bg',
        'swiperPaginationProgressbarSize': '--btpro-swiper-pagination-progressbar-size',
        'swiperPaginationBulletSize': '--btpro-swiper-pagination-bullet-size',
        'swiperPaginationBulletWidth': '--btpro-swiper-pagination-bullet-width',
        'swiperPaginationBulletHeight': '--btpro-swiper-pagination-bullet-height',
        'swiperPaginationBulletInactiveColor': '--btpro-swiper-pagination-bullet-inactive-color',
        'swiperPaginationBulletInactiveOpacity': '--btpro-swiper-pagination-bullet-inactive-opacity',
        'swiperPaginationBulletOpacity': '--btpro-swiper-pagination-bullet-opacity',
        'swiperPaginationBulletGapH': '--btpro-swiper-pagination-bullet-gap-h',
        'swiperPaginationBulletGapV': '--btpro-swiper-pagination-bullet-gap-v',
        
        // Scrollbar
        'swiperScrollbarSize': '--btpro-swiper-scrollbar-size',
        'swiperScrollbarBgColor': '--btpro-swiper-scrollbar-bg-color',
        'swiperScrollbarDragBgColor': '--btpro-swiper-scrollbar-drag-bg-color',
        'swiperScrollbarRadius': '--btpro-swiper-scrollbar-radius',
        'swiperScrollbarTop': '--btpro-swiper-scrollbar-top',
        'swiperScrollbarBottom': '--btpro-swiper-scrollbar-bottom',
        'swiperScrollbarLeft': '--btpro-swiper-scrollbar-left',
        'swiperScrollbarRight': '--btpro-swiper-scrollbar-right',
        'swiperScrollbarSidesOffset': '--btpro-swiper-scrollbar-sides-offset'
      };

      // Her data attribute'ı kontrol et ve CSS değişkenine set et
      Object.keys(cssVarMap).forEach(dataKey => {
        const dataAttr = 'data-' + this.camelToKebab(dataKey);
        const value = this.getAttribute(dataAttr);
        
        if (value !== null) {
          this.style.setProperty(cssVarMap[dataKey], value);
        }
      });
    }

    /**
     * camelCase'i kebab-case'e çevirir
     * Örnek: swiperNavSize -> swiper-nav-size
     */
    camelToKebab(str) {
      return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
    }

    checkBreakpoint() {
      const shouldBeActive = this.shouldActivate();

      if (shouldBeActive && !this.currentBreakpointActive) {
        this.initSwiper();
        this.currentBreakpointActive = true;
      } else if (!shouldBeActive && this.currentBreakpointActive) {
        this.destroySwiper();
        this.currentBreakpointActive = false;
      }
    }

    shouldActivate() {
      const breakpoints = this.breakpoint.split(',');
      let shouldActivate = false;

      breakpoints.forEach((bp) => {
        const mediaQuery = this.getMediaQuery(bp.trim());
        if (mediaQuery === 'all' || window.matchMedia(mediaQuery).matches) {
          shouldActivate = true;
        }
      });

      return shouldActivate;
    }

    getMediaQuery(breakpoint) {
      const breakpointMap = {
        'all': 'all',
        'xs': '(max-width: 550px)',
        'sm': '(max-width: 749px)',
        'md': '(max-width: 989px)',
        'lg': '(max-width: 1199px)',
        'xl': '(max-width: 1439px)',
        'u-sm': '(min-width: 750px)',
        'u-md': '(min-width: 990px)',
        'u-lg': '(min-width: 1200px)',
        'u-xl': '(min-width: 1440px)'
      };

      return breakpointMap[breakpoint] || 'all';
    }

    initSwiper() {
      if (this.swiperInstance) {
        return; // Already initialized
      }

      const slides = this.swiperElement.querySelectorAll('.swiper-slide');
      if (slides.length === 0) {
        console.warn('BtproThemeSwiperSlide: No .swiper-slide elements found');
        return;
      }

      // Get configuration from data attributes
      const config = this.getSwiperConfig();

      try {
        this.swiperInstance = new Swiper(this.swiperElement, config);
        
        // Dispatch custom event when swiper is initialized
        this.dispatchEvent(new CustomEvent('swiper:initialized', {
          detail: { swiper: this.swiperInstance },
          bubbles: true
        }));
      } catch (error) {
        console.error('BtproThemeSwiperSlide: Error initializing Swiper', error);
      }
    }

    getSwiperConfig() {
      // Base configuration with defaults
      const config = {
        direction: this.dataset.direction || 'horizontal',
        slidesPerView: this.parseNumber(this.dataset.slidesPerView, 1),
        spaceBetween: this.parseNumber(this.dataset.spaceBetween, 0),
        speed: this.parseNumber(this.dataset.speed, 600),
        loop: this.dataset.loop === 'true',
        autoplay: false,
        grabCursor: this.dataset.grabCursor !== 'false',
        watchSlidesProgress: true,
        allowTouchMove: this.dataset.allowTouchMove !== 'false',
        touchRatio: this.parseNumber(this.dataset.touchRatio, 1),
        threshold: this.parseNumber(this.dataset.threshold, 5),
        resistance: this.dataset.resistance !== 'false',
        resistanceRatio: this.parseNumber(this.dataset.resistanceRatio, 0.85),
        followFinger: this.dataset.followFinger !== 'false',
        simulateTouch: this.dataset.simulateTouch !== 'false',
      };

      // Pagination
      const paginationType = this.dataset.typePagination || this.dataset.pagination;
      const paginationEl = this.swiperElement.querySelector('.swiper-pagination');
      
      if (paginationEl && paginationType) {
        config.pagination = {
          el: paginationEl,
          type: paginationType,
          clickable: paginationType !== 'progressbar',
          dynamicBullets: this.dataset.dynamicBullets === 'true',
          dynamicMainBullets: this.parseNumber(this.dataset.dynamicMainBullets, 1),
        };

        // Custom pagination renderer if needed
        if (this.dataset.paginationCustomClass) {
          config.pagination.bulletClass = this.dataset.paginationCustomClass;
        }
      }

      // Navigation arrows
      const nextEl = this.swiperElement.querySelector('.swiper-button-next');
      const prevEl = this.swiperElement.querySelector('.swiper-button-prev');
      
      if (nextEl && prevEl) {
        config.navigation = {
          nextEl: nextEl,
          prevEl: prevEl,
        };

        // Custom navigation classes
        if (this.dataset.navNextClass) {
          config.navigation.nextEl = this.dataset.navNextClass;
        }
        if (this.dataset.navPrevClass) {
          config.navigation.prevEl = this.dataset.navPrevClass;
        }
      }

      // Scrollbar
      const scrollbarEl = this.swiperElement.querySelector('.swiper-scrollbar');
      if (scrollbarEl) {
        config.scrollbar = {
          el: scrollbarEl,
          draggable: this.dataset.scrollbarDraggable !== 'false',
          hide: this.dataset.scrollbarHide === 'true',
        };
      }

      // Effect (fade, cube, coverflow, flip, cards, creative)
      if (this.dataset.effect) {
        config.effect = this.dataset.effect;
        
        // Effect-specific options
        if (this.dataset.effect === 'fade') {
          config.fadeEffect = {
            crossFade: this.dataset.fadeCrossFade === 'true'
          };
        } else if (this.dataset.effect === 'cube') {
          config.cubeEffect = {
            slideShadows: this.dataset.cubeSlideShadows !== 'false',
            shadow: this.dataset.cubeShadow !== 'false',
            shadowOffset: this.parseNumber(this.dataset.cubeShadowOffset, 20),
            shadowScale: this.parseNumber(this.dataset.cubeShadowScale, 0.94)
          };
        } else if (this.dataset.effect === 'coverflow') {
          config.coverflowEffect = {
            rotate: this.parseNumber(this.dataset.coverflowRotate, 50),
            stretch: this.parseNumber(this.dataset.coverflowStretch, 0),
            depth: this.parseNumber(this.dataset.coverflowDepth, 100),
            modifier: this.parseNumber(this.dataset.coverflowModifier, 1),
            slideShadows: this.dataset.coverflowSlideShadows !== 'false'
          };
        } else if (this.dataset.effect === 'flip') {
          config.flipEffect = {
            slideShadows: this.dataset.flipSlideShadows !== 'false',
            limitRotation: this.dataset.flipLimitRotation !== 'false'
          };
        }
      }

      // Autoplay
      if (this.dataset.autoplay === 'true') {
        config.autoplay = {
          delay: this.parseNumber(this.dataset.autoplayDelay, 3000),
          disableOnInteraction: this.dataset.autoplayDisableOnInteraction === 'true',
          pauseOnMouseEnter: this.dataset.autoplayPauseOnMouseEnter === 'true',
          reverseDirection: this.dataset.autoplayReverse === 'true',
          stopOnLastSlide: this.dataset.autoplayStopOnLast === 'true',
        };
      }

      // Responsive breakpoints
      if (this.dataset.breakpointConfig) {
        try {
          config.breakpoints = JSON.parse(this.dataset.breakpointConfig);
        } catch (e) {
          console.warn('BtproThemeSwiperSlide: Invalid breakpoint config JSON', e);
        }
      } else {
        // Auto-generate breakpoints from data attributes if provided
        config.breakpoints = this.getBreakpointsConfig();
      }

      // Centered slides
      if (this.dataset.centeredSlides === 'true') {
        config.centeredSlides = true;
        config.centeredSlidesBounds = this.dataset.centeredSlidesBounds === 'true';
      }

      // Free mode
      if (this.dataset.freeMode === 'true') {
        config.freeMode = {
          enabled: true,
          sticky: this.dataset.freeModeSticky === 'true',
          momentum: this.dataset.freeModeMomentum !== 'false',
          momentumBounce: this.dataset.freeModeMomentumBounce !== 'false',
          momentumRatio: this.parseNumber(this.dataset.freeModeMomentumRatio, 1),
          momentumVelocityRatio: this.parseNumber(this.dataset.freeModeMomentumVelocityRatio, 1),
          minimumVelocity: this.parseNumber(this.dataset.freeModeMinimumVelocity, 0.02),
        };
      }

      // Keyboard control
      if (this.dataset.keyboard === 'true') {
        config.keyboard = {
          enabled: true,
          onlyInViewport: this.dataset.keyboardOnlyInViewport !== 'false',
        };
      }

      // Mousewheel control
      if (this.dataset.mousewheel === 'true') {
        config.mousewheel = {
          enabled: true,
          forceToAxis: this.dataset.mousewheelForceToAxis === 'true',
          sensitivity: this.parseNumber(this.dataset.mousewheelSensitivity, 1),
          releaseOnEdges: this.dataset.mousewheelReleaseOnEdges === 'true',
        };
      }

      // Lazy loading
      if (this.dataset.lazy === 'true') {
        config.lazy = {
          loadPrevNext: this.dataset.lazyLoadPrevNext === 'true',
          loadPrevNextAmount: this.parseNumber(this.dataset.lazyLoadPrevNextAmount, 1),
          loadOnTransitionStart: this.dataset.lazyLoadOnTransitionStart === 'true',
        };
      }

      // Events callbacks (if needed, can be extended)
      if (this.dataset.onSlideChange) {
        config.on = {
          slideChange: () => {
            this.dispatchEvent(new CustomEvent('swiper:slideChange', {
              detail: { swiper: this.swiperInstance },
              bubbles: true
            }));
          }
        };
      }

      return config;
    }

    getBreakpointsConfig() {
      const breakpoints = {};
      
      // Mobile breakpoint (default: 320)
      if (this.dataset.breakpointMobile) {
        const mobileConfig = this.parseBreakpointString(this.dataset.breakpointMobile);
        if (mobileConfig) breakpoints[320] = mobileConfig;
      }

      // Tablet breakpoint (default: 768)
      if (this.dataset.breakpointTablet) {
        const tabletConfig = this.parseBreakpointString(this.dataset.breakpointTablet);
        if (tabletConfig) breakpoints[768] = tabletConfig;
      }

      // Desktop breakpoint (default: 1024)
      if (this.dataset.breakpointDesktop) {
        const desktopConfig = this.parseBreakpointString(this.dataset.breakpointDesktop);
        if (desktopConfig) breakpoints[1024] = desktopConfig;
      }

      // Large desktop breakpoint (default: 1440)
      if (this.dataset.breakpointLarge) {
        const largeConfig = this.parseBreakpointString(this.dataset.breakpointLarge);
        if (largeConfig) breakpoints[1440] = largeConfig;
      }

      return Object.keys(breakpoints).length > 0 ? breakpoints : null;
    }

    parseBreakpointString(str) {
      // Format: "slidesPerView:2,spaceBetween:20,loop:true"
      try {
        const config = {};
        const pairs = str.split(',');
        pairs.forEach(pair => {
          const [key, value] = pair.split(':').map(s => s.trim());
          if (key && value !== undefined) {
            // Try to parse as number
            if (!isNaN(value) && value !== '') {
              config[key] = parseFloat(value);
            } else if (value === 'true' || value === 'false') {
              config[key] = value === 'true';
            } else {
              config[key] = value;
            }
          }
        });
        return config;
      } catch (e) {
        console.warn('BtproThemeSwiperSlide: Invalid breakpoint string format', e);
        return null;
      }
    }

    parseNumber(value, defaultValue) {
      if (!value) return defaultValue;
      const parsed = parseInt(value, 10);
      return isNaN(parsed) ? defaultValue : parsed;
    }

    destroySwiper() {
      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.resizeHandler);
      this.destroySwiper();
    }

    // Public API methods
    slideNext() {
      if (this.swiperInstance) {
        this.swiperInstance.slideNext();
      }
    }

    slidePrev() {
      if (this.swiperInstance) {
        this.swiperInstance.slidePrev();
      }
    }

    slideTo(index) {
      if (this.swiperInstance) {
        this.swiperInstance.slideTo(index);
      }
    }

    update() {
      if (this.swiperInstance) {
        this.swiperInstance.update();
      }
    }
  }

  // Register the custom element
  if (typeof customElements !== 'undefined') {
    customElements.define('btpro-theme-swiper-slide', BtproThemeSwiperSlide);
  } else {
    console.error('BtproThemeSwiperSlide: Custom Elements not supported in this browser');
  }

  /**
   * ============================================
   * GLOBAL API EXPOSURE
   * ============================================
   */

  // Global API'yi oluştur veya mevcut olana ekle
  if (typeof window.GlameTheme === 'undefined') {
    window.GlameTheme = {};
  }

  window.GlameTheme.Swiper = {
    // Helper function to get swiper instance from element
    getInstance: function(element) {
      if (element && element.swiperInstance) {
        return element.swiperInstance;
      }
      return null;
    },
    // Helper function to find all swiper instances
    getAllInstances: function() {
      const elements = document.querySelectorAll('btpro-theme-swiper-slide');
      const instances = [];
      elements.forEach(el => {
        if (el.swiperInstance) {
          instances.push(el.swiperInstance);
        }
      });
      return instances;
    }
  };

})();
